import {
  Add as AddIcon,
} from '../icons';
import React from 'react';
import {
  Typography,
  Chip,
  Box,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { IMAGE_PLACEHOLDER, API_URL } from '../../config';
import { useCurrencyFormatter } from '../../contexts/CurrencyContext';
import { usePublicTheme } from '../../contexts/PublicThemeContext';
import { useCart } from '../../contexts/CartContext';
import { useNotification } from '../../contexts/NotificationContext';
import { getHoverColor, hexToRgba } from '../../utils/colorUtils';
import OptimizedImage from '../common/OptimizedImage';

const CardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})(({ theme, disabled }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  position: 'relative',
  cursor: disabled ? 'default' : 'pointer',
  borderRadius: theme.spacing(2.5),
  padding: theme.spacing(1),
  background: disabled ? 'rgba(245, 245, 245, 0.95)' : 'rgba(255, 255, 255, 0.95)',
  border: disabled ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: disabled ? '0 4px 12px rgba(0, 0, 0, 0.04)' : '0 10px 30px rgba(0, 0, 0, 0.08)',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  opacity: disabled ? 0.7 : 1,
  [theme.breakpoints.up('md')]: {
    borderRadius: theme.spacing(3.5),
    padding: theme.spacing(1.5),
  },
  ...(!disabled && {
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      transform: 'translateY(-6px) scale(1.02)',
      boxShadow: '0 25px 60px rgba(0, 0, 0, 0.18), 0 10px 25px rgba(0, 0, 0, 0.12)',
      '& .menu-image': {
        transform: 'scale(1.1)',
      },
      '& .add-button': {
        transform: 'scale(1.15)',
        boxShadow: '0 10px 35px rgba(102, 126, 234, 0.6)',
      },
      [theme.breakpoints.up('md')]: {
        transform: 'translateY(-10px) scale(1.03)',
      },
    },
    '&:active': {
      transform: 'translateY(-3px) scale(1.01)',
      [theme.breakpoints.up('md')]: {
        transform: 'translateY(-6px) scale(1.015)',
      },
    },
  }),
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 140,
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.spacing(2),
  background: 'linear-gradient(135deg, #f8fafc 0%, #e3e8f0 100%)',
  boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.04)',
  [theme.breakpoints.up('sm')]: {
    height: 180,
  },
  [theme.breakpoints.up('md')]: {
    height: 280,
    borderRadius: theme.spacing(3),
  },
}));

const menuImageSx = (theme) => ({
  borderRadius: theme.spacing(3),
});

const AddButton = styled(IconButton)(({ theme }) => ({
  color: '#fff',
  width: 36,
  height: 36,
  borderRadius: '50%',
  border: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  [theme.breakpoints.up('sm')]: {
    width: 40,
    height: 40,
  },
  [theme.breakpoints.up('md')]: {
    width: 56,
    height: 56,
  },
  '&:active': {
    transform: 'scale(1.1)',
  },
  '&.Mui-disabled': {
    background: '#e2e8f0',
    color: '#a0aec0',
    border: 'none',
  },
}));

const FeaturedChip = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1.5),
  left: theme.spacing(1.5),
  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
  color: '#fff',
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontWeight: 800,
  fontSize: '0.65rem',
  height: 26,
  borderRadius: theme.spacing(1.5),
  boxShadow: '0 4px 12px rgba(255, 107, 107, 0.45)',
  backdropFilter: 'blur(15px)',
  border: 'none',
  transition: 'all 0.3s ease',
  [theme.breakpoints.up('md')]: {
    top: theme.spacing(3),
    left: theme.spacing(3),
    fontSize: '0.8rem',
    height: 36,
    borderRadius: theme.spacing(2.5),
    boxShadow: '0 8px 25px rgba(255, 107, 107, 0.55)',
  },
  '&:hover': {
    transform: 'scale(1.08)',
    boxShadow: '0 10px 30px rgba(255, 107, 107, 0.65)',
  },
  '& .MuiChip-label': {
    padding: '0 10px',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontWeight: 800,
    [theme.breakpoints.up('md')]: {
      padding: '0 16px',
    },
  },
}));

const MenuCard = ({
  item,
  onAddToCart,
  onViewDetails,
  showCategory = true,
  showDescription = true,
  compact = false,
  orderingDisabled = false
}) => {
  const {
    id,
    name,
    price,
    image,
    is_available,
    is_featured,
    has_options
  } = item;

  const { formatCurrency } = useCurrencyFormatter();
  const { primaryColor } = usePublicTheme();
  const { getItemQuantity, addItem } = useCart();
  const { addNotification } = useNotification();

  // Helper function to construct full image URL
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return IMAGE_PLACEHOLDER;
    if (imageUrl.startsWith('http')) return imageUrl; // Already a full URL
    return `${API_URL}${imageUrl}`; // Prepend API_URL for relative URLs
  };

  // Get current quantity in cart (for items without options)
  const currentQuantity = has_options ? 0 : getItemQuantity(id, []);

  const handleAddToCart = (event) => {
    event.stopPropagation();
    if (has_options || currentQuantity === 0) {
      // Open dialog for items with options or when adding first item
      onViewDetails(item);
    } else {
      // Direct add for simple items already in cart
      addItem(item, 1, []);
      const newQuantity = currentQuantity + 1;
      addNotification(`Updated ${name} quantity to ${newQuantity}`, 'primary');
    }
  };

  // Render add button with quantity indicator
  const renderAddButton = () => {
    const showQuantity = !has_options && currentQuantity > 0;

    return (
      <AddButton
        onClick={handleAddToCart}
        disabled={!is_available}
        size="medium"
        sx={{
          background: primaryColor,
          boxShadow: `0 4px 20px ${hexToRgba(primaryColor, 0.4)}, 0 2px 8px rgba(0, 0, 0, 0.1)`,
          '&:hover': {
            background: getHoverColor(primaryColor, 'darken', 15),
            boxShadow: `0 6px 25px ${hexToRgba(primaryColor, 0.5)}, 0 4px 12px rgba(0, 0, 0, 0.15)`,
          },
        }}
      >
        {showQuantity ? (
          <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem', fontWeight: 'bold' }}>
            {currentQuantity}
          </Box>
        ) : (
          <AddIcon />
        )}
      </AddButton>
    );
  };

  const isDisabled = !is_available;

  return (
    <CardContainer
      data-primary-color={primaryColor}
      className="add-button"
      disabled={isDisabled}
      onClick={() => {
        if (!isDisabled) {
          onViewDetails(item);
        }
      }}
    >
      <ImageContainer sx={{ position: 'relative' }}>
        <OptimizedImage
          className="menu-image"
          src={getImageUrl(image)}
          alt={name}
          fallbackSrc={IMAGE_PLACEHOLDER}
          sx={(theme) => ({
            ...menuImageSx(theme),
            ...(isDisabled ? { filter: 'grayscale(100%)' } : {}),
          })}
        />
        {is_featured && !isDisabled && (
          <FeaturedChip
            label="Featured"
            size="small"
          />
        )}
        {isDisabled && (
          <Chip
            label="Unavailable"
            size="small"
            sx={{
              position: 'absolute',
              top: (theme) => ({ xs: theme.spacing(1.5), md: theme.spacing(3) }),
              left: (theme) => ({ xs: theme.spacing(1.5), md: theme.spacing(3) }),
              background: 'rgba(0, 0, 0, 0.6)',
              color: '#fff',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800,
              fontSize: { xs: '0.65rem', md: '0.8rem' },
              height: { xs: 26, md: 36 },
              borderRadius: (theme) => ({ xs: theme.spacing(1.5), md: theme.spacing(2.5) }),
              backdropFilter: 'blur(15px)',
              border: 'none',
              '& .MuiChip-label': {
                padding: { xs: '0 10px', md: '0 16px' },
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 800,
              },
            }}
          />
        )}
      </ImageContainer>

      <Box sx={{ mt: { xs: 1, sm: 1.5, md: 3 }, px: { xs: 1, sm: 1.5, md: 2.5 }, pb: { xs: 0.5, md: 0 }, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800,
              fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.2rem' },
              mb: { xs: 0.5, sm: 0.75, md: 1.5 },
              lineHeight: 1.3,
              color: isDisabled ? '#9ca3af' : '#1f2937',
              letterSpacing: '-0.01em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {name}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.4rem' },
              color: isDisabled ? '#9ca3af' : '#1f2937',
              letterSpacing: '-0.02em',
            }}
          >
            {formatCurrency(price)}
          </Typography>
        </Box>
        {!isDisabled && !orderingDisabled && (
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            {renderAddButton()}
          </Box>
        )}
      </Box>
    </CardContainer>
  );
};

export default MenuCard;
